import isNumber from "lodash-es/isNumber";
import { Reducer } from "redux";

import {
    BLOGS_SHORT_MESSAGE_CREATE_SAVE_SUCCESS,
    BlogsShortMessageCreateSaveSuccessAction,
} from "@/mk2/apps/blogs/containers/BlogsShortMessageCreate/BlogsShortMessageCreate.actions";
import {
    COMMENTS_FETCH_FAILURE,
    COMMENTS_FETCH_REQUEST,
    COMMENTS_FETCH_SUCCESS,
    CommentsFetchFailureAction,
    CommentsFetchRequestAction,
    CommentsFetchSuccessAction,
} from "@/mk2/apps/blogs/containers/Comments/Comments.actions";
import {
    BLOG_POST_CREATE_SUCCESS,
    BlogPostCreateSuccessAction,
    PROFILE_DELETE_AVATAR_BY_ADMIN_SUCCESS,
    PROFILE_DETAIL_FETCH_FAILURE,
    PROFILE_DETAIL_FETCH_REQUEST,
    PROFILE_DETAIL_FETCH_SUCCESS,
    PROFILE_EXPAND_AVATAR_GRID_SUCCESS,
    PROFILE_EXPAND_GROUP_LIST_SUCCESS,
    PROFILE_FEED_FETCH_FAILURE,
    PROFILE_FEED_FETCH_REQUEST,
    PROFILE_FEED_FETCH_SUCCESS,
    PROFILE_SET_LIKE_USER_SUCCESS,
    ProfileDeleteAvatarByAdminSuccessAction,
    ProfileDetailFetchFailureAction,
    ProfileDetailFetchRequestAction,
    ProfileDetailFetchSuccessAction,
    ProfileExpandAvatarGridFailureAction,
    ProfileExpandAvatarGridRequestAction,
    ProfileExpandAvatarGridSuccessAction,
    ProfileExpandGroupListFailureAction,
    ProfileExpandGroupListRequestAction,
    ProfileExpandGroupListSuccessAction,
    ProfileFeedFetchFailureAction,
    ProfileFeedFetchRequestAction,
    ProfileFeedFetchSuccessAction,
    ProfileSetLikeUserSuccessAction,
} from "@/mk2/apps/blogs/containers/ProfileDetail/ProfileDetail.actions";
import {
    PROFILE_PRODUCTS_ADD_PRODUCT_API_FAILURE,
    PROFILE_PRODUCTS_ADD_PRODUCT_API_REQUEST,
    PROFILE_PRODUCTS_ADD_PRODUCT_API_SUCCESS,
    PROFILE_PRODUCTS_REMOVE_PRODUCT_API_FAILURE,
    PROFILE_PRODUCTS_REMOVE_PRODUCT_API_REQUEST,
    PROFILE_PRODUCTS_REMOVE_PRODUCT_API_SUCCESS,
    ProfileProductsAddProductApiFailureAction,
    ProfileProductsAddProductApiRequestAction,
    ProfileProductsAddProductApiSuccessAction,
    ProfileProductsRemoveProductApiFailureAction,
    ProfileProductsRemoveProductApiRequestAction,
    ProfileProductsRemoveProductApiSuccessAction,
    ProfileProductsSearchProductsApiSuccessAction,
} from "@/mk2/apps/blogs/containers/ProfileProducts/ProfileProducts.actions";
import { KidEntity } from "@/mk2/apps/settings/schemas";
import { tupdate } from "@/mk2/helpers/types";
import { errorMessage } from "@/mk2/reducers/errorMessage";
import { LoadingState, loadingState } from "@/mk2/reducers/loadingState";
import {
    GroupEntity,
    PhotoEntity,
    PostVisibility,
    ProVendorEntity,
    ProfileProductType,
    StrollerEntity,
    StrollerUserReviewEntity,
    UserEntity,
} from "@/mk2/schemas";

export interface ProfileFeedPageState {
    postIds: number[];
    errorMessage: string;
    loadingState: LoadingState;
    loadedAt: number;
}

export const initialProfileFeedPageState: ProfileFeedPageState = {
    postIds: [],
    errorMessage: null,
    loadingState: LoadingState.INIT,
    loadedAt: null,
};

export interface ProfileFeedVirtualPageState {
    errorMessage: string;
    loadingState: LoadingState;
}

export const initialProfileFeedVirtualPageState: ProfileFeedVirtualPageState = {
    errorMessage: null,
    loadingState: LoadingState.INIT,
};

export interface ProfileFeedPostsState {
    numberOfPages: number;
    pages: { [pageNum: number]: ProfileFeedPageState };
    virtualPages: { [pageName: string]: ProfileFeedVirtualPageState }; // pageName = 'first'|'last'...
}

export const initialProfileFeedPostsState: ProfileFeedPostsState = {
    numberOfPages: 1,
    pages: {},
    virtualPages: {},
};

type PostsReducerAction =
    | ProfileFeedFetchRequestAction
    | ProfileFeedFetchSuccessAction
    | ProfileFeedFetchFailureAction
    | BlogPostCreateSuccessAction
    | BlogsShortMessageCreateSaveSuccessAction;

const profileFeedPostsReducer: Reducer<ProfileFeedPostsState, PostsReducerAction> = (
    state = initialProfileFeedPostsState,
    action,
) => {
    switch (action.type) {
        case PROFILE_FEED_FETCH_REQUEST:
        case PROFILE_FEED_FETCH_FAILURE:
            if (isNumber(action.pageRef)) {
                return tupdate(state, {
                    pages: tupdate(state.pages, {
                        [action.pageRef]: tupdate(
                            state.pages[action.pageRef] || initialProfileFeedPageState,
                            {
                                loadingState: loadingState(undefined, action),
                                errorMessage: errorMessage(undefined, action),
                            },
                        ),
                    }),
                });
            } else {
                return tupdate(state, {
                    virtualPages: tupdate(state.virtualPages, {
                        [action.pageRef]: tupdate(
                            state.pages[action.pageRef] || initialProfileFeedVirtualPageState,
                            {
                                loadingState: loadingState(undefined, action),
                                errorMessage: errorMessage(undefined, action),
                            },
                        ),
                    }),
                });
            }

        case BLOG_POST_CREATE_SUCCESS:
        case BLOGS_SHORT_MESSAGE_CREATE_SAVE_SUCCESS: {
            const profileFeedPageState = state.pages[1] || initialProfileFeedPageState;
            const profileFeedVirtualPageState =
                state.virtualPages.first || initialProfileFeedVirtualPageState;
            return tupdate(state, {
                // update state of page under both - virtual name and real index
                virtualPages: tupdate(state.virtualPages, {
                    first: tupdate(profileFeedVirtualPageState, {
                        loadingState: loadingState(undefined, action),
                        errorMessage: errorMessage(undefined, action),
                    }),
                }),
                pages: tupdate(state.pages, {
                    [1]: tupdate(profileFeedPageState, {
                        postIds: [action.response.result.post].concat(profileFeedPageState.postIds),
                        loadingState: loadingState(undefined, action),
                        errorMessage: errorMessage(undefined, action),
                        loadedAt: new Date().getTime(),
                    }),
                }),
            });
        }

        case PROFILE_FEED_FETCH_SUCCESS:
            if (!isNumber(action.page)) {
                throw new Error("Page must be a number");
            }

            return tupdate(state, {
                numberOfPages: action.response.result.numberOfPages,
                // update state of page under both - virtual name and real index
                virtualPages: isNumber(action.pageRef)
                    ? state.virtualPages
                    : tupdate(state.virtualPages, {
                          [action.pageRef]: tupdate(
                              state.virtualPages[action.pageRef] ||
                                  initialProfileFeedVirtualPageState,
                              {
                                  loadingState: loadingState(undefined, action),
                                  errorMessage: errorMessage(undefined, action),
                              },
                          ),
                      }),
                pages: tupdate(state.pages, {
                    [action.page]: tupdate(
                        state.pages[action.page] || initialProfileFeedPageState,
                        {
                            postIds: action.response.result.posts,
                            loadingState: loadingState(undefined, action),
                            errorMessage: errorMessage(undefined, action),
                            loadedAt: new Date().getTime(),
                        },
                    ),
                }),
            });

        default:
            return state;
    }
};

export const queryKey = (postType, query): string => `posts:${postType}:${query || ""}`;

const searchKey = (action) => queryKey(action.postType, action.query);

export interface ProfileDetailData {
    ambassadorBrandGroupId: number;
    ambassadorBrandLogo: PhotoEntity;
    ambassadorBrandName: string;
    displayVisibilityAlert: boolean;
    followers: {
        count: number;
        expanded: boolean;
        hasMore: boolean;
        users: UserEntity[];
    };
    following: {
        count: number;
        expanded: boolean;
        hasMore: boolean;
        users: UserEntity[];
    };
    isLegacyBlocked: boolean;
    kids: KidEntity[];
    memberOfGroups: {
        count: number;
        hasMore: boolean;
        groups: GroupEntity[];
    };
    pregnancyDaysCounter: number;
    pregnancyWeekNumber: number;
    user: UserEntity;
    userAboutMeText: string;
    userAge: number;
    userAvatarPhoto: PhotoEntity;
    userIsInactive: boolean;
    userIsLiked: boolean;
    userOnlineStatus: string;
    userRegisteredOn: string;
    userResidence: string;
    userResidenceCountyId: number;
    userStats: {
        blogsAlbumsCount: number;
        blogsArticlesCount: number;
        blogsArticleDraftsCount?: number;
        blogsShortMessagesCount: number;
        marketProductsCount: number;
    };
    userStrollers: StrollerEntity[];
    userStrollersReviews: StrollerUserReviewEntity[];
    userVendorData: ProVendorEntity;
}

export interface ProfileDetailDataNormalized {
    ambassadorBrandGroupId: number;
    ambassadorBrandLogo: number;
    ambassadorBrandName: string;
    defaultPostVisibility: PostVisibility;
    displayVisibilityAlert: boolean;
    followers: {
        count: number;
        expanded: boolean;
        hasMore: boolean;
        users: number[];
    };
    following: {
        count: number;
        expanded: boolean;
        hasMore: boolean;
        users: number[];
    };
    isLegacyBlocked: boolean;
    kids: number[];
    memberOfGroups: {
        count: number;
        hasMore: boolean;
        groups: number[];
    };
    pregnancyDaysCounter: number;
    pregnancyWeekNumber: number;
    user: number;
    userAboutMeText: string;
    userAge: number;
    userAvatarPhoto: number;
    userIsInactive: boolean;
    userIsLiked: boolean;
    userOnlineStatus: string;
    userRegisteredOn: string;
    userResidence: string;
    userResidenceCountyId: number;
    userStats: {
        blogsAlbumsCount: number;
        blogsArticlesCount: number;
        blogsArticleDraftsCount?: number;
        blogsShortMessagesCount: number;
        marketProductsCount: number;
    };
    userStrollers: number[];
    userStrollersReviews: number[];
    userVendorData: number;
}

export interface ProfileDetailState {
    detailErrorMessage: string;
    detailLoadingState: LoadingState;
    feedErrorMessage: string;
    feedLoadingState: LoadingState;
    hasComplaints: boolean;
    profileDetailData: ProfileDetailDataNormalized;
    searches: {
        [query: string]: ProfileFeedPostsState;
    };
}

export const initialProfileDetailState: ProfileDetailState = {
    detailErrorMessage: null,
    detailLoadingState: LoadingState.INIT,
    feedErrorMessage: null,
    feedLoadingState: LoadingState.INIT,
    hasComplaints: false,
    profileDetailData: {
        ambassadorBrandGroupId: null,
        ambassadorBrandLogo: null,
        ambassadorBrandName: null,
        defaultPostVisibility: null,
        displayVisibilityAlert: false,
        followers: {
            count: 0,
            expanded: false,
            hasMore: false,
            users: [],
        },
        following: {
            count: 0,
            expanded: false,
            hasMore: false,
            users: [],
        },
        isLegacyBlocked: false,
        kids: [],
        memberOfGroups: {
            count: 0,
            hasMore: false,
            groups: [],
        },
        pregnancyDaysCounter: null,
        pregnancyWeekNumber: null,
        user: null,
        userAboutMeText: null,
        userAge: null,
        userAvatarPhoto: null,
        userIsInactive: false,
        userIsLiked: false,
        userOnlineStatus: null,
        userRegisteredOn: null,
        userResidence: null,
        userResidenceCountyId: null,
        userStats: {
            blogsAlbumsCount: 0,
            blogsArticlesCount: 0,
            blogsArticleDraftsCount: 0,
            blogsShortMessagesCount: 0,
            marketProductsCount: null,
        },
        userStrollers: [],
        userStrollersReviews: [],
        userVendorData: null,
    },
    searches: {},
};

const stateKey = (action: ProfileDetailReducerActionNotRelatedToUser) => action.username;

export interface ProfileDetailsState {
    commentsLoadingState: LoadingState;
    errorMessage: string;
    profiles: {
        [username: string]: ProfileDetailState;
    };
}

export const initialProfileDetailsState: ProfileDetailsState = {
    commentsLoadingState: LoadingState.INIT,
    errorMessage: null,
    profiles: {},
};

declare type ProfileDetailReducerAction =
    | BlogPostCreateSuccessAction
    | BlogsShortMessageCreateSaveSuccessAction
    | CommentsFetchRequestAction
    | CommentsFetchSuccessAction
    | CommentsFetchFailureAction
    | ProfileDetailFetchRequestAction
    | ProfileDetailFetchSuccessAction
    | ProfileDetailFetchFailureAction
    | ProfileExpandAvatarGridRequestAction
    | ProfileExpandAvatarGridSuccessAction
    | ProfileExpandAvatarGridFailureAction
    | ProfileExpandGroupListRequestAction
    | ProfileExpandGroupListSuccessAction
    | ProfileExpandGroupListFailureAction
    | ProfileFeedFetchRequestAction
    | ProfileFeedFetchSuccessAction
    | ProfileFeedFetchFailureAction
    | ProfileDeleteAvatarByAdminSuccessAction
    | ProfileSetLikeUserSuccessAction
    | ProfileProductsRemoveProductApiRequestAction
    | ProfileProductsRemoveProductApiSuccessAction
    | ProfileProductsRemoveProductApiFailureAction
    | ProfileProductsAddProductApiRequestAction
    | ProfileProductsAddProductApiSuccessAction
    | ProfileProductsAddProductApiFailureAction
    | ProfileProductsSearchProductsApiSuccessAction;

declare type ProfileDetailReducerActionNotRelatedToUser =
    | BlogPostCreateSuccessAction
    | BlogsShortMessageCreateSaveSuccessAction
    | ProfileDetailFetchRequestAction
    | ProfileDetailFetchSuccessAction
    | ProfileDetailFetchFailureAction
    | ProfileExpandAvatarGridRequestAction
    | ProfileExpandAvatarGridSuccessAction
    | ProfileExpandAvatarGridFailureAction
    | ProfileExpandGroupListRequestAction
    | ProfileExpandGroupListSuccessAction
    | ProfileExpandGroupListFailureAction
    | ProfileFeedFetchRequestAction
    | ProfileFeedFetchSuccessAction
    | ProfileFeedFetchFailureAction
    | ProfileDeleteAvatarByAdminSuccessAction
    | ProfileSetLikeUserSuccessAction
    | ProfileProductsRemoveProductApiRequestAction
    | ProfileProductsRemoveProductApiSuccessAction
    | ProfileProductsRemoveProductApiFailureAction
    | ProfileProductsAddProductApiRequestAction
    | ProfileProductsAddProductApiSuccessAction
    | ProfileProductsAddProductApiFailureAction;

const profileDetailsReducer: Reducer<ProfileDetailsState, ProfileDetailReducerAction> = (
    state = initialProfileDetailsState,
    action,
) => {
    switch (action.type) {
        case COMMENTS_FETCH_REQUEST:
        case COMMENTS_FETCH_SUCCESS:
        case COMMENTS_FETCH_FAILURE:
            return tupdate(state, {
                commentsLoadingState: loadingState(undefined, action),
                errorMessage: errorMessage(undefined, action),
            });

        case PROFILE_PRODUCTS_ADD_PRODUCT_API_REQUEST:
        case PROFILE_PRODUCTS_ADD_PRODUCT_API_FAILURE:
        case PROFILE_PRODUCTS_REMOVE_PRODUCT_API_REQUEST:
        case PROFILE_PRODUCTS_REMOVE_PRODUCT_API_FAILURE:
        case PROFILE_DETAIL_FETCH_REQUEST:
        case PROFILE_DETAIL_FETCH_FAILURE: {
            const key = stateKey(action);
            const profileDetailState = state.profiles[key] || initialProfileDetailState;
            return tupdate(state, {
                profiles: tupdate(state.profiles, {
                    [key]: tupdate(profileDetailState, {
                        detailErrorMessage: errorMessage(undefined, action),
                        detailLoadingState: loadingState(undefined, action),
                    }),
                }),
            });
        }

        case PROFILE_DETAIL_FETCH_SUCCESS: {
            const key = stateKey(action);
            const profileDetailState = state.profiles[key] || initialProfileDetailState;

            return tupdate(state, {
                profiles: tupdate(state.profiles, {
                    [key]: tupdate(profileDetailState, {
                        detailErrorMessage: errorMessage(undefined, action),
                        detailLoadingState: loadingState(undefined, action),
                        profileDetailData: tupdate(profileDetailState.profileDetailData, {
                            ...action.response.result,
                            userStats: tupdate(profileDetailState.profileDetailData.userStats, {
                                ...action.response.result.userStats,
                            }),
                        }),
                    }),
                }),
            });
        }

        case PROFILE_FEED_FETCH_REQUEST:
        case PROFILE_FEED_FETCH_FAILURE: {
            const key = stateKey(action);
            const profileDetailState = state.profiles[key] || initialProfileDetailState;
            return tupdate(state, {
                profiles: tupdate(state.profiles, {
                    [key]: tupdate(profileDetailState, {
                        feedErrorMessage: errorMessage(undefined, action),
                        feedLoadingState: loadingState(undefined, action),
                        searches: tupdate(profileDetailState.searches, {
                            [searchKey(action)]: profileFeedPostsReducer(
                                profileDetailState.searches[searchKey(action)],
                                action,
                            ),
                        }),
                    }),
                }),
            });
        }

        case PROFILE_FEED_FETCH_SUCCESS: {
            const key = stateKey(action);
            const profileDetailState = state.profiles[key] || initialProfileDetailState;
            return tupdate(state, {
                profiles: tupdate(state.profiles, {
                    [key]: tupdate(state.profiles[key] || initialProfileDetailState, {
                        feedErrorMessage: errorMessage(undefined, action),
                        feedLoadingState: loadingState(undefined, action),
                        searches: tupdate(profileDetailState.searches, {
                            [searchKey(action)]: profileFeedPostsReducer(
                                profileDetailState.searches[searchKey(action)],
                                action,
                            ),
                        }),
                        hasComplaints: action.hasComplaints,
                        profileDetailData: tupdate(profileDetailState.profileDetailData, {
                            displayVisibilityAlert: action.response.result.displayVisibilityAlert,
                            userStats: tupdate(profileDetailState.profileDetailData.userStats, {
                                ...action.response.result.userStats,
                            }),
                        }),
                    }),
                }),
            });
        }

        case BLOG_POST_CREATE_SUCCESS:
        case BLOGS_SHORT_MESSAGE_CREATE_SAVE_SUCCESS: {
            const key = stateKey(action);
            const profileDetailState = state.profiles[key] || initialProfileDetailState;
            return tupdate(state, {
                profiles: tupdate(state.profiles, {
                    [key]: tupdate(state.profiles[key] || initialProfileDetailState, {
                        searches: tupdate(profileDetailState.searches, {
                            [searchKey(action)]: profileFeedPostsReducer(
                                profileDetailState.searches[searchKey(action)],
                                action,
                            ),
                        }),
                        profileDetailData: tupdate(profileDetailState.profileDetailData, {
                            defaultPostVisibility: action.postFd.publicVisibility
                                ? PostVisibility.Public
                                : PostVisibility.Authenticated,
                        }),
                    }),
                }),
            });
        }

        case PROFILE_SET_LIKE_USER_SUCCESS: {
            const key = stateKey(action);
            const profileDetailState = state.profiles[key] || initialProfileDetailState;
            return tupdate(state, {
                profiles: tupdate(state.profiles, {
                    [key]: tupdate(profileDetailState, {
                        detailErrorMessage: errorMessage(undefined, action),
                        detailLoadingState: loadingState(undefined, action),
                        profileDetailData: tupdate(profileDetailState.profileDetailData, {
                            userIsLiked: action.like,
                        }),
                    }),
                }),
            });
        }

        case PROFILE_DELETE_AVATAR_BY_ADMIN_SUCCESS: {
            const key = stateKey(action);
            const profileDetailState = state.profiles[key] || initialProfileDetailState;
            return tupdate(state, {
                profiles: tupdate(state.profiles, {
                    [key]: tupdate(profileDetailState, {
                        detailErrorMessage: errorMessage(undefined, action),
                        detailLoadingState: loadingState(undefined, action),
                        profileDetailData: tupdate(profileDetailState.profileDetailData, {
                            userAvatarPhoto: null,
                        }),
                    }),
                }),
            });
        }

        case PROFILE_PRODUCTS_ADD_PRODUCT_API_SUCCESS: {
            const key = stateKey(action);
            const profileDetailState = state.profiles[key] || initialProfileDetailState;
            return tupdate(state, {
                profiles: tupdate(state.profiles, {
                    [key]: tupdate(profileDetailState, {
                        detailErrorMessage: errorMessage(undefined, action),
                        detailLoadingState: loadingState(undefined, action),
                        profileDetailData: tupdate(profileDetailState.profileDetailData, {
                            userStrollers:
                                action.productType === ProfileProductType.STROLLER
                                    ? [
                                          ...profileDetailState.profileDetailData.userStrollers,
                                          action.response.result.stroller,
                                      ]
                                    : profileDetailState.profileDetailData.userStrollers,
                        }),
                    }),
                }),
            });
        }

        case PROFILE_PRODUCTS_REMOVE_PRODUCT_API_SUCCESS: {
            const key = stateKey(action);
            const profileDetailState = state.profiles[key] || initialProfileDetailState;
            return tupdate(state, {
                profiles: tupdate(state.profiles, {
                    [key]: tupdate(profileDetailState, {
                        detailErrorMessage: errorMessage(undefined, action),
                        detailLoadingState: loadingState(undefined, action),
                        profileDetailData: tupdate(profileDetailState.profileDetailData, {
                            userStrollers:
                                action.productType === ProfileProductType.STROLLER
                                    ? profileDetailState.profileDetailData.userStrollers.filter(
                                          (i) => i !== action.productId,
                                      )
                                    : profileDetailState.profileDetailData.userStrollers,
                        }),
                    }),
                }),
            });
        }

        case PROFILE_EXPAND_AVATAR_GRID_SUCCESS: {
            const key = stateKey(action);
            const profileDetailState = state.profiles[key] || initialProfileDetailState;
            const followingOrFollowersState =
                profileDetailState.profileDetailData[action.avatarGridType];
            return tupdate(state, {
                profiles: tupdate(state.profiles, {
                    [key]: tupdate(profileDetailState, {
                        detailErrorMessage: errorMessage(undefined, action),
                        detailLoadingState: loadingState(undefined, action),
                        profileDetailData: tupdate(profileDetailState.profileDetailData, {
                            [action.avatarGridType]: tupdate(followingOrFollowersState, {
                                hasMore: action.response.result.hasMore,
                                users: action.response.result.users,
                                expanded: true,
                            }),
                        }),
                    }),
                }),
            });
        }

        case PROFILE_EXPAND_GROUP_LIST_SUCCESS: {
            const key = stateKey(action);
            const profileDetailState = state.profiles[key] || initialProfileDetailState;
            const memberOfGroupsState = profileDetailState.profileDetailData.memberOfGroups;
            return tupdate(state, {
                profiles: tupdate(state.profiles, {
                    [key]: tupdate(profileDetailState, {
                        detailErrorMessage: errorMessage(undefined, action),
                        detailLoadingState: loadingState(undefined, action),
                        profileDetailData: tupdate(profileDetailState.profileDetailData, {
                            memberOfGroups: tupdate(memberOfGroupsState, {
                                hasMore: action.response.result.hasMore,
                                groups: action.response.result.groups,
                            }),
                        }),
                    }),
                }),
            });
        }

        default:
            return state;
    }
};

export default profileDetailsReducer;
